import { useEffect, useState } from "react";
import { Mail } from "lucide-react";
import { Button, Card, CardContent, CardHeader, CardTitle, Switch, LoadingState } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { portal, type PortalNotificationGroup } from "../../../lib/portal";

/**
 * CRM module email notifications per linked client (contracts, invoices,
 * support and so on).
 *
 * A different table from the card above, and deliberately so: these preferences
 * are per CLIENT rather than per user, because the mail is addressed to a
 * company. One person can be the contact for two clients and want different
 * things from each.
 */
export function ClientMatrixCard() {
  const t = useT();
  const [groups, setGroups] = useState<PortalNotificationGroup[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    void portal.notifications().then((r) => setGroups(r.clients)).catch(() => setGroups([]));
  }, []);

  function toggle(clientId: string, key: string, val: boolean) {
    setGroups((gs) =>
      gs ? gs.map((g) => (g.clientId === clientId
        ? { ...g, categories: g.categories.map((c) => (c.key === key ? { ...c, enabled: val } : c)) }
        : g)) : gs);
    setSaved(null);
  }

  async function save(clientId: string) {
    const group = groups?.find((g) => g.clientId === clientId);
    if (!group) return;
    setBusy(clientId); setSaved(null);
    try {
      await portal.setNotifications(clientId, group.categories);
      setSaved(clientId);
      setTimeout(() => setSaved((s) => (s === clientId ? null : s)), 1500);
    } finally { setBusy(null); }
  }

  // Not linked to any client, so there is nothing for this card to say.
  if (groups !== null && groups.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Mail className="size-4 text-primary" /> {t("clientNotifications.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground">{t("clientNotifications.intro")}</p>
        {groups === null ? (
          <LoadingState label={t("common.loading")} />
        ) : (
          groups.map((g) => (
            <div key={g.clientId} className="rounded-xl border border-border p-4">
              {groups.length > 1 && <div className="mb-3 text-sm font-semibold text-foreground">{g.clientName}</div>}
              <ul className="divide-y divide-border/60">
                {g.categories.map((c) => (
                  <li key={c.key} className="flex items-center justify-between gap-4 py-2.5">
                    <div>
                      <div className="text-sm font-medium text-foreground">{t(`clientNotifications.cat.${c.key}`)}</div>
                      <div className="text-xs text-muted-foreground">{t(`clientNotifications.catDesc.${c.key}`)}</div>
                    </div>
                    <Switch
                      checked={c.enabled}
                      onCheckedChange={(v) => toggle(g.clientId, c.key, v)}
                      aria-label={t(`clientNotifications.cat.${c.key}`)}
                    />
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex justify-end">
                <Button size="sm" onClick={() => save(g.clientId)} loading={busy === g.clientId}>
                  {saved === g.clientId ? t("common.saved") : t("common.save")}
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
